import axios, { type AxiosRequestConfig, type AxiosError } from "axios";
import { tokenStorage } from "@/auth/tokenStorage";
import { normalizeDeep } from "@/shared/utils/persian";

/**
 * ADR-071 Rule FE-03: تنها جایی که منطق attach/refresh توکن می‌نشیند.
 * orval این فایل را به‌عنوان "custom mutator" استفاده می‌کند.
 *
 * AM-051-03: هدر X-Tenant-Id برای کاربران عادی ارسال نمی‌شود (tenant از نشست سمت سرور
 * تعیین می‌شود). فقط Superuser — که ذاتاً tenant ندارد — هدر را می‌فرستد (تبصره‌ی RES-121).
 * کد خطای پایدار TENANT_CONTEXT_REQUIRED یک‌بار منجر به خروج و بازگشت به صفحه‌ی لاگین می‌شود.
 */

const baseURL = import.meta.env.VITE_API_BASE_URL || "";

const TENANT_CONTEXT_REQUIRED = "TENANT_CONTEXT_REQUIRED";

export const axiosInstance = axios.create({
  baseURL,
});

// فقط Superuser هدر tenant را می‌فرستد (و فقط اگر tenant انتخاب کرده باشد)
function tenantHeaderFor(): Record<string, string> {
  if (!tokenStorage.isSuperuser()) return {};
  const tenantId = tokenStorage.getTenantId();
  return tenantId ? { "X-Tenant-Id": tenantId } : {};
}

// --- درخواست: attach کردن access token + نرمال‌سازی ی/ک ---
axiosInstance.interceptors.request.use((config) => {
  const token = tokenStorage.getAccessToken();
  if (token) {
    config.headers.set("Authorization", `Bearer ${token}`);
  }

  const tenantHeader = tenantHeaderFor();
  for (const [key, value] of Object.entries(tenantHeader)) {
    config.headers.set(key, value);
  }

  // نرمال‌سازی حروف عربی ي/ك → فارسی ی/ک
  // 1) JSON body
  if (config.data && typeof config.data === "object") {
    config.data = normalizeDeep(config.data);
  }
  // 2) Query params
  if (config.params && typeof config.params === "object") {
    config.params = normalizeDeep(config.params);
  }

  return config;
});

// --- پاسخ: روی 401، یک بار تلاش برای refresh و retry ---
let refreshPromise: Promise<string | null> | null = null;

async function performRefresh(): Promise<string | null> {
  try {
    const refreshToken = tokenStorage.getRefreshToken();

    // AM-051-03: دیگر به tenantId ذخیره‌شده وابسته نیستیم (کاربر عادی)
    if (!refreshToken) {
      tokenStorage.clear();
      return null;
    }

    const payload = { refresh_token: refreshToken };

    const response = await axios.post<{ access_token: string; refresh_token?: string }>(
      `${baseURL}/api/v1/identity/refresh/`,
      payload,
      { headers: tenantHeaderFor() },
    );

    tokenStorage.setAccessToken(response.data.access_token);
    if (response.data.refresh_token) {
      tokenStorage.setRefreshToken(response.data.refresh_token);
    }
    return response.data.access_token;
  } catch {
    tokenStorage.clear();
    return null;
  }
}

function errorCode(error: AxiosError): string | undefined {
  const data = error.response?.data as { code?: string } | undefined;
  return data?.code;
}

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as (AxiosRequestConfig & { _retry?: boolean }) | undefined;

    // AM-051-03: جلوگیری از حلقه‌ی ریدایرکت — فقط یک‌بار خروج؛ پس از clear دیگر توکنی نیست
    if (errorCode(error) === TENANT_CONTEXT_REQUIRED) {
      if (tokenStorage.getAccessToken() || tokenStorage.getRefreshToken()) {
        tokenStorage.clear();
        window.dispatchEvent(new CustomEvent("auth:logout"));
      }
      return Promise.reject(error);
    }

    if (error.response?.status === 403) {
      console.warn("403 Forbidden on API call:", original?.url);
      // Removed window.location.href = "/" to avoid aggressive UX breaks.
      return Promise.reject(error);
    }

    if (error.response?.status !== 401 || !original || original._retry) {
      return Promise.reject(error);
    }

    original._retry = true;

    if (!refreshPromise) {
      refreshPromise = performRefresh().finally(() => {
        refreshPromise = null;
      });
    }

    const newToken = await refreshPromise;
    if (!newToken) {
      window.dispatchEvent(new CustomEvent("auth:logout"));
      return Promise.reject(error);
    }

    original.headers = {
      ...original.headers,
      Authorization: `Bearer ${newToken}`,
    };
    return axiosInstance.request(original);
  },
);

export const customAxiosInstance = <T>(config: AxiosRequestConfig): Promise<T> => {
  return axiosInstance.request<T>(config).then((res) => res.data);
};
